import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { HeartHandshake, Calendar, Users, TrendingUp, Loader2, Filter, Download, Share2, Copy, Check } from "lucide-react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);
const fmtDate = (d) => d ? new Date(d + "T00:00").toLocaleDateString("pt-BR") : "—";

export const FilantropiaReportView = () => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [shareUrl, setShareUrl] = useState("");
  const [copyOk, setCopyOk] = useState(false);

  const generateShare = async () => {
    try {
      const res = await axios.post(`${API}/reports/filantropia/share`);
      const fullUrl = `${window.location.origin}${res.data.public_url}`;
      setShareUrl(fullUrl);
      // auto copy
      try {
        await navigator.clipboard.writeText(fullUrl);
        setCopyOk(true);
        setTimeout(() => setCopyOk(false), 2000);
        toast.success("Link público gerado e copiado! Compartilhe em WhatsApp/redes.");
      } catch { toast.success("Link público gerado."); }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao gerar link público.");
    }
  };

  const revokeShare = async () => {
    if (!window.confirm("Revogar todos os links públicos ativos? Quem já os tem não conseguirá mais acessar.")) return;
    try {
      await axios.delete(`${API}/reports/filantropia/share`);
      setShareUrl("");
      toast.success("Links revogados.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao revogar link.");
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const res = await axios.get(`${API}/reports/filantropia`, { params });
      setReport(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao carregar relatório.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const exportPdf = () => {
    if (!report) return;
    const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
    const now = new Date().toLocaleString("pt-BR");
    doc.setFontSize(16); doc.setTextColor(212, 175, 55);
    doc.text("Prestação de Contas — Bolsa de Beneficência", 14, 18);
    doc.setFontSize(9); doc.setTextColor(80, 80, 80);
    doc.text(`Gerado em ${now}`, 14, 24);
    const p = report.period;
    if (p.from || p.to) {
      doc.text(`Período: ${p.from || "início"} até ${p.to || "hoje"}`, 14, 29);
    }

    doc.setFontSize(11); doc.setTextColor(0);
    doc.text(`Saldo atual do fundo: ${fmt(report.bolsa.saldo_atual)}`, 14, 38);

    autoTable(doc, {
      startY: 44,
      head: [["Indicador", "Valor"]],
      body: [
        ["Total arrecadado (receitas)", fmt(report.resumo.total_receita)],
        ["Total distribuído (auxílios)", fmt(report.resumo.total_despesa)],
        ["Saldo de movimento no período", fmt(report.resumo.saldo_movimento)],
        ["Tronco de Beneficência (soma sessões)", fmt(report.resumo.total_arrecadado_tronco)],
        ["Sessões com Tronco", String(report.resumo.sessoes_com_tronco)],
        ["Quantidade de auxílios", String(report.resumo.quantidade_auxilios)],
        ["Beneficiários únicos", String(report.resumo.quantidade_beneficiarios)],
      ],
      styles: { fontSize: 9 },
      headStyles: { fillColor: [212, 175, 55], textColor: [0, 0, 0] },
    });

    if (report.tronco_por_sessao.length) {
      autoTable(doc, {
        head: [["Data", "Sessão", "Título", "Tronco"]],
        body: report.tronco_por_sessao.map(s => [fmtDate(s.date), `nº ${s.session_number || "—"}`, s.title || "—", fmt(s.trunk_amount)]),
        styles: { fontSize: 8 }, headStyles: { fillColor: [168, 85, 247], textColor: [255, 255, 255] },
        margin: { top: 8 },
      });
    }

    if (report.beneficiarios.length) {
      autoTable(doc, {
        head: [["Beneficiário", "Tipo", "Auxílios", "Total Recebido"]],
        body: report.beneficiarios.map(b => [b.name || "—", b.type || "—", String(b.count), fmt(b.total_received)]),
        styles: { fontSize: 8 }, headStyles: { fillColor: [16, 185, 129], textColor: [255, 255, 255] },
        margin: { top: 8 },
      });
    }
    doc.save(`filantropia_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  if (loading) {
    return (
      <div className="luxury-card p-12 text-center">
        <Loader2 className="w-8 h-8 mx-auto animate-spin text-[#D4AF37]" />
        <p className="text-sm text-slate-400 mt-3">Compilando prestação de contas…</p>
      </div>
    );
  }
  if (!report) return null;

  return (
    <div className="space-y-6" data-testid="filantropia-report-view">
      {/* Header + Period filter */}
      <div className="luxury-card p-5 flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-purple-300 uppercase tracking-widest font-bold">
            <HeartHandshake className="w-3.5 h-3.5" />
            <span>Fundo Filantrópico • Prestação de Contas</span>
          </div>
          <h2 className="text-xl font-serif font-bold text-white mt-1">{report.bolsa.name}</h2>
          <div className="text-3xl font-mono font-bold text-[#D4AF37] mt-2" data-testid="filantropia-saldo">
            {fmt(report.bolsa.saldo_atual)}
          </div>
          <p className="text-[10px] text-slate-500 mt-1">Saldo atual do fundo</p>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <label className="text-[9px] uppercase text-slate-400 tracking-wider font-semibold block mb-1">De</label>
            <input data-testid="filantropia-date-from" type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
              className="bg-[#131C2E] border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#D4AF37] outline-none" />
          </div>
          <div>
            <label className="text-[9px] uppercase text-slate-400 tracking-wider font-semibold block mb-1">Até</label>
            <input data-testid="filantropia-date-to" type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
              className="bg-[#131C2E] border border-slate-700 rounded-lg px-2 py-1.5 text-xs text-white focus:border-[#D4AF37] outline-none" />
          </div>
          <button data-testid="filantropia-filter-apply" onClick={load}
            className="flex items-center gap-1.5 bg-[#131C2E] hover:bg-[#1E293B] border border-slate-700 text-slate-200 font-bold px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider">
            <Filter className="w-3 h-3" /> Aplicar
          </button>
          <button data-testid="filantropia-export-pdf" onClick={exportPdf}
            className="flex items-center gap-1.5 bg-[#D4AF37] hover:bg-[#C5A028] text-slate-950 font-bold px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider">
            <Download className="w-3 h-3" /> PDF
          </button>
          <button data-testid="filantropia-share" onClick={generateShare}
            className="flex items-center gap-1.5 bg-purple-500 hover:bg-purple-400 text-white font-bold px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider">
            <Share2 className="w-3 h-3" /> Compartilhar
          </button>
        </div>
      </div>

      {shareUrl && (
        <div data-testid="filantropia-share-url" className="luxury-card p-4 border-l-4 border-l-purple-500 bg-purple-500/5 flex flex-wrap items-center justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase text-purple-300 tracking-widest font-bold mb-1">Link Público de Transparência</div>
            <code className="text-[11px] text-slate-300 break-all font-mono">{shareUrl}</code>
            <p className="text-[10px] text-slate-500 mt-1 italic">Beneficiários aparecem anonimamente ("Auxílio filantrópico") — LGPD preservada.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={async () => { await navigator.clipboard.writeText(shareUrl); setCopyOk(true); setTimeout(()=>setCopyOk(false),2000); }}
              className="flex items-center gap-1.5 bg-[#131C2E] hover:bg-[#1E293B] border border-slate-700 text-slate-200 font-bold px-3 py-1.5 rounded-lg text-[10px] uppercase tracking-wider">
              {copyOk ? <><Check className="w-3 h-3 text-emerald-400" /> Copiado</> : <><Copy className="w-3 h-3" /> Copiar</>}
            </button>
            <button onClick={revokeShare}
              className="text-[10px] uppercase font-bold tracking-wider text-rose-400 hover:text-rose-300">Revogar</button>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCard icon={<TrendingUp className="w-4 h-4 text-emerald-400" />} label="Arrecadado" value={fmt(report.resumo.total_receita)} tone="emerald" />
        <KpiCard icon={<TrendingUp className="w-4 h-4 text-rose-400 rotate-180" />} label="Distribuído" value={fmt(report.resumo.total_despesa)} tone="rose" />
        <KpiCard icon={<HeartHandshake className="w-4 h-4 text-purple-400" />} label="Tronco (sessões)" value={fmt(report.resumo.total_arrecadado_tronco)} tone="purple" />
        <KpiCard icon={<Users className="w-4 h-4 text-cyan-400" />} label="Beneficiários" value={String(report.resumo.quantidade_beneficiarios)} tone="cyan" />
      </div>

      {/* Tronco por sessão */}
      <div className="luxury-card p-5">
        <h3 className="font-serif font-bold text-sm text-white flex items-center gap-2 mb-3">
          <Calendar className="w-4 h-4 text-purple-400" />
          Tronco de Beneficência por Sessão ({report.tronco_por_sessao.length})
        </h3>
        {report.tronco_por_sessao.length === 0 ? (
          <p className="text-xs text-slate-500 italic">Nenhum Tronco arrecadado no período.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#080C14] border-b border-slate-800 text-slate-400 uppercase tracking-wider">
                <tr><th className="text-left py-2 px-3">Data</th><th className="text-left py-2 px-3">Sessão</th><th className="text-left py-2 px-3">Título</th><th className="text-right py-2 px-3">Valor</th></tr>
              </thead>
              <tbody>
                {report.tronco_por_sessao.map(s => (
                  <tr key={s.session_id} className="border-b border-slate-800/50 hover:bg-[#131C2E]" data-testid={`tronco-row-${s.session_id}`}>
                    <td className="py-2 px-3 font-mono text-[10px] text-slate-300">{fmtDate(s.date)}</td>
                    <td className="py-2 px-3 font-mono text-[10px]">nº {s.session_number || "—"}</td>
                    <td className="py-2 px-3 text-slate-300 truncate max-w-[280px]">{s.title || "—"}</td>
                    <td className="py-2 px-3 text-right font-mono font-bold text-purple-300">{fmt(s.trunk_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Beneficiários */}
      <div className="luxury-card p-5">
        <h3 className="font-serif font-bold text-sm text-white flex items-center gap-2 mb-3">
          <Users className="w-4 h-4 text-emerald-400" />
          Beneficiários no Período ({report.beneficiarios.length})
        </h3>
        {report.beneficiarios.length === 0 ? (
          <p className="text-xs text-slate-500 italic">Nenhum auxílio distribuído no período.</p>
        ) : (
          <div className="space-y-2">
            {report.beneficiarios.map((b, idx) => (
              <details key={idx} className="rounded-xl bg-[#080C14] border border-slate-800 overflow-hidden">
                <summary className="p-3 cursor-pointer hover:bg-[#131C2E] flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${
                      b.type === "irmão" ? "bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40" :
                      b.type === "externo" ? "bg-cyan-500/20 text-cyan-300 border border-cyan-500/40" :
                      "bg-slate-700/40 text-slate-400 border border-slate-700"
                    }`}>{b.type || "-"}</span>
                    <span className="text-sm text-white font-semibold truncate">{b.name || "—"}</span>
                    <span className="text-[10px] text-slate-500">({b.count} auxílio{b.count > 1 ? "s" : ""})</span>
                  </div>
                  <span className="font-mono font-bold text-emerald-300 shrink-0">{fmt(b.total_received)}</span>
                </summary>
                <div className="p-3 bg-[#0B111E] text-[11px] text-slate-400 space-y-1 border-t border-slate-800">
                  {b.events.map(ev => (
                    <div key={ev.id} className="flex items-center justify-between gap-3">
                      <span>{fmtDate(ev.date)} • {ev.description || ev.category || "—"}</span>
                      <span className="font-mono text-rose-300">-{fmt(ev.amount)}</span>
                    </div>
                  ))}
                </div>
              </details>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const KpiCard = ({ icon, label, value, tone }) => {
  const toneMap = {
    emerald: "text-emerald-300",
    rose: "text-rose-300",
    purple: "text-purple-300",
    cyan: "text-cyan-300",
  };
  return (
    <div className="luxury-card p-4">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10px] text-slate-400 uppercase tracking-wider font-semibold">{label}</span>
        {icon}
      </div>
      <div className={`text-lg font-mono font-bold ${toneMap[tone] || "text-white"}`}>{value}</div>
    </div>
  );
};
